import {
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonFab,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { personCircleOutline, swapHorizontalOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  EVENTO_FISCAL_ACTUANDO,
  FiscalActuando,
  esGerenteDeOperaciones,
  esRutaDeFiscal,
  guardarFiscalActuando,
  obtenerFiscalActuando,
} from "src/config/gerenteOperaciones";
import { IFiscalAsignado, listarFiscalesAsignados } from "src/services/visitas";

interface Props {
  isAuth: boolean;
  cargo?: string;
}

// Selector "Actuando como fiscal" de la Gerencia de Operaciones.
// En las pantallas de fiscal, si no eligió fiscal se le pide uno; si ya eligió,
// queda un chip flotante para ver con quién está trabajando y cambiarlo.
const GerenteFiscalSelector: React.FC<Props> = ({ isAuth, cargo }) => {
  const history = useHistory();
  const location = useLocation();
  const [fiscal, setFiscal] = useState<FiscalActuando | null>(obtenerFiscalActuando());
  const [abierto, setAbierto] = useState(false);
  const [fiscales, setFiscales] = useState<IFiscalAsignado[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const visible = isAuth && esGerenteDeOperaciones(cargo) && esRutaDeFiscal(location.pathname);

  useEffect(() => {
    const sincronizar = () => setFiscal(obtenerFiscalActuando());
    window.addEventListener(EVENTO_FISCAL_ACTUANDO, sincronizar);
    return () => window.removeEventListener(EVENTO_FISCAL_ACTUANDO, sincronizar);
  }, []);

  // Sin fiscal elegido no hay datos que mostrar: se pide elegir uno al entrar.
  useEffect(() => {
    if (visible && !fiscal) setAbierto(true);
  }, [visible, fiscal]);

  useEffect(() => {
    if (!abierto || fiscales.length) return;
    let activo = true;
    setCargando(true);
    setError("");
    listarFiscalesAsignados()
      .then((lista) => {
        if (activo) setFiscales(lista);
      })
      .catch(() => {
        if (activo) setError("No se pudo cargar la lista de fiscales.");
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => {
      activo = false;
    };
  }, [abierto, fiscales.length]);

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return fiscales;
    return fiscales.filter((f) => f.nombre.toLowerCase().includes(texto) || f.cedula.includes(texto));
  }, [fiscales, busqueda]);

  const elegir = (elegido: IFiscalAsignado) => {
    guardarFiscalActuando({ cedula: elegido.cedula, nombre: elegido.nombre });
    setAbierto(false);
    // La pantalla ya cargó sus datos sin fiscal (o con el anterior): se recarga.
    window.location.reload();
  };

  // Se llama una sola vez, al terminar de cerrarse el modal. Si cerró sin elegir
  // fiscal, la pantalla de fiscal no tiene datos que mostrar: vuelve al inicio.
  const alCerrar = () => {
    setAbierto(false);
    if (!obtenerFiscalActuando()) history.push("/home");
  };

  if (!visible) return null;

  return (
    <>
      {fiscal && (
        <IonFab vertical="bottom" horizontal="start" slot="fixed">
          <IonChip color="primary" onClick={() => setAbierto(true)}>
            <IonIcon icon={personCircleOutline} />
            <IonLabel>Actuando como: {fiscal.nombre || fiscal.cedula}</IonLabel>
            <IonIcon icon={swapHorizontalOutline} />
          </IonChip>
        </IonFab>
      )}

      <IonModal isOpen={abierto} onDidDismiss={alCerrar} backdropDismiss={Boolean(fiscal)}>
        <IonHeader>
          <IonToolbar color="primary">
            <IonTitle>¿Con qué fiscal trabajás?</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setAbierto(false)}>{fiscal ? "Cerrar" : "Volver"}</IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              value={busqueda}
              placeholder="Buscar por nombre o cédula"
              onIonChange={(e) => setBusqueda(e.detail.value || "")}
            />
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {cargando && (
            <div className="ion-text-center ion-padding">
              <IonSpinner />
            </div>
          )}
          {error && (
            <IonText color="danger">
              <p className="ion-padding">{error}</p>
            </IonText>
          )}
          {!cargando && !error && (
            <IonList>
              {filtrados.map((f) => (
                <IonItem
                  key={f.cedula}
                  button
                  detail={false}
                  color={fiscal?.cedula === f.cedula ? "light" : undefined}
                  onClick={() => elegir(f)}
                >
                  <IonLabel>
                    <h2>{f.nombre || f.cedula}</h2>
                    <p>CI {f.cedula}</p>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          )}
        </IonContent>
      </IonModal>
    </>
  );
};

export default GerenteFiscalSelector;
